import React, { useEffect, useMemo, useReducer, useState } from 'react';
import logo from './logo.svg';
import './App.css';
import { Routes, Route } from "react-router-dom";

import { Dashboard } from './screens/dashboard.js';
import { Residents, AddResident } from './screens/residents.js';
import { Fees, FeeDetails } from './screens/fees.js';
import { Albums } from './screens/albums.js';
import { Gallery } from './screens/gallery.js';
import { NewsLetters } from './screens/newsletters.js';
import { Events } from './screens/events.js';
import { ResidentStatusReport } from './screens/reports.js';
import { Complaints, ViewComplaint } from './screens/complaints.js';
import { MassCommunications } from './screens/mass_communications.js';
import { ResidentGroups } from './screens/resident_groups.js';
import { Violations, NewViolation, EditViolation, ViewViolation } from './screens/violations.js';
import { AccRequests, ViewAccRequest } from './screens/acc_requests.js';
import { MOM } from './screens/mom.js';
import { EmailEvents } from './screens/email_events.js';
import { UserManagement } from './screens/users.js';
import { MyProfile } from './screens/profile.js';
import { Login } from './screens/login.js';
import { NotFound } from './screens/404.js';
import { AuthContext, MenuContext } from './screens/constants.js';

function App() {
  const [menus, setMenus] = useState({});
  const [state, dispatch] = useReducer(
    (prevState, action) => {
      switch (action.type) {
        case 'RESTORE_TOKEN':
          return {
            ...prevState,
            userToken: action.token,
            isLoading: false,
          };
        case 'SIGN_IN':
          return {
            ...prevState,
            isSignout: false,
            userToken: action.token,
          };
        case 'SIGN_OUT':
          return {
            ...prevState,
            isSignout: true,
            userToken: null,
          };
      }
    },
    {
      isLoading: true,
      isSignout: false,
      userToken: null,
    }
  );

  useEffect(() => {
    // Fetch the token from storage then navigate to our appropriate place
    const bootstrapAsync = async () => {
      let userToken;
      let userRole;

      try {
        userToken = await sessionStorage.getItem('userToken');
        userRole  = await sessionStorage.getItem('userRole');
      } catch (e) {
        console.log(e);
      }
      setMenus(JSON.parse(userRole));
      dispatch({ type: 'RESTORE_TOKEN', token: userToken, role: userRole });
    };
    bootstrapAsync();
  }, []);

  const authContext = useMemo(
    () => ({
      signIn: async (data) => {
        fetch('/login', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            'username' :data.useremail,
            'password' :data.password
          })
        })
        .then(response => response.json())
        .then(result => {
          console.log('Success:', result);
          sessionStorage.setItem("userName", result.username);
          sessionStorage.setItem("userToken", result.email);
          setMenus(result.menus);
          sessionStorage.setItem("userRole", JSON.stringify(result.menus));
          dispatch({ type: 'SIGN_IN', token: result.email });
        })
        .catch((e) => {
          console.error('Error:', e);
        });
      },
      signOut: async() => {
        sessionStorage.removeItem("userToken")
        sessionStorage.removeItem("userRole")
        dispatch({ type: 'SIGN_OUT', token: null});
      },
      signUp: async (data) => {
        // In a production app, we need to send user data to server and get a token
        // We will also need to handle errors if sign up failed
        // After getting token, we need to persist the token using `SessionStorage`
        // In the example, we'll use a dummy token
        dispatch({ type: 'SIGN_IN', token: 'dummy-auth-token' });
      },
    }),
    []
  );

  return (
    <AuthContext.Provider value={authContext}>
      <MenuContext.Provider value={menus}>
        <div className="App">
          <Routes>
            {state.isLoading ? (
              // We haven't finished checking for the token yet
              <>
                <Route path="/" element={<Dashboard />} />
              </>
              //Change with Loading GIF
            ) : state.userToken == null ? (
              <>
                <Route path="/" element={<Dashboard />} />
                <Route path="/login" element={<Login />} />
                <Route path="*" element={<NotFound />} />
              </>
            ) :  (
              <>
                <Route path="/" element={<Dashboard />} />
                <Route path="login" element={<Login />} />
                <Route path="profile" element={<MyProfile />} />
                {menus.albums &&
                  <>
                    <Route path="albums" element={<Albums />} />
                    <Route path="photo-gallery/:albumId" element={<Gallery />} />
                  </>
                }
                {menus.acc_requests &&
                  <>
                    <Route path="acc-requests" element={<AccRequests />} />
                    <Route path="acc-request/:id" element={<ViewAccRequest />} />
                  </>
                }
                {menus.mass_communications &&
                  <>
                    <Route path="mass-communications" element={<MassCommunications />} />
                  </>
                }
                {menus.complaints &&
                  <>
                    <Route path="complaints" element={<Complaints />} />
                    <Route path="complaint/:id" element={<ViewComplaint />} />
                  </>
                }
                {menus.violations &&
                  <>
                    <Route path="violations" element={<Violations />} />
                    <Route path="add-violation" element={<NewViolation />} />
                    <Route path="edit-violation/:id" element={<EditViolation />} />
                    <Route path="violation/:id" element={<ViewViolation />} />
                  </>
                }
                {menus.events &&
                  <Route path="events" element={<Events />} />
                }
                {menus.fees &&
                  <>
                    <Route path="fees" element={<Fees />} />
                    <Route path="fee-details/:feeId" element={<FeeDetails />} />
                  </>
                }
                {menus.mom &&
                  <Route path="mom" element={<MOM />} />
                }
                {menus.newsletters &&
                  <Route path="newsletters" element={<NewsLetters />} />
                }
                {menus.residents &&
                  <>
                    <Route path="residents" element={<Residents />} />
                    <Route path="add-resident" element={<AddResident />} />
                    <Route path="edit-resident/:id" element={<AddResident />} />
                    <Route path="resident-status" element={<ResidentStatusReport />} />
                    <Route path="resident-groups" element={<ResidentGroups />} />
                  </>
                }
                {menus.email_events &&
                  <Route path="email-events" element={<EmailEvents />} />
                }
                {menus.is_admin &&
                  <Route path="user-management" element={<UserManagement />} />
                }
                <Route path="*" element={<NotFound />} />
              </>
            )
          }
          </Routes>
        </div>
      </MenuContext.Provider>
    </AuthContext.Provider>
  );
}

export default App;
